import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReducersIndex from "reduxModules/ducks";
import useFetch from "hooks/useFetch";

import Payload from "components/services/Payload";

import useSetURLparams from "hooks/useSetURLParams";

import isBoolean from "lodash/isBoolean";
import { getErrorState, getReload } from "reduxModules/ducks/ui";
import { checkStorageServiceHealth, getDetailedErrorMessage } from "utilities/storageServiceUtils";
import { useToast } from "contexts/ToastContext";
import { setIsLoadingDesign } from "reduxModules/middleware/loadingFlag";

// API endpoint constant
const RESTAPI_ENDPOINT = `${process.env.REACT_APP_STORAGE_SERVICE_URL}/api/v1/designs`;

const Load = ({params, setProjectLoaded, setSplashOpen, target, setFirstTimeUser}) => {


  const [load, setLoad] = useState(params.includes('savekey'));
  const [payload, setPayload] = useState(Payload());
  const [reset, setReset] = useState(false);

  const errorState = useSelector(getErrorState);
  const reload = useSelector(getReload);
  const isLatest = useSelector(state => state.ui.isLatest);
  const currentTool = useSelector(state => state.ui.tool);
  const { showError } = useToast();

  useSetURLparams();
   
  let url = `${RESTAPI_ENDPOINT}/${payload.savekey}/content`;
  let httpMethod = "GET";
   
  const { connecting, response, error, fetchData} = useFetch(url, payload, httpMethod);

  const dispatch = useDispatch();

  // Hide the splash
  useEffect(() => {
    if (load) setSplashOpen(false);
  // eslint-disable-next-line
  }, [load]);

  // Show loading dialog
  useEffect(() => {
    if (connecting) dispatch(ReducersIndex.uiReducers.openModal('loading'));
  // eslint-disable-next-line
  }, [connecting]);

  // Fetch data 
  useEffect(() => {
    if (load) fetchData();
  // eslint-disable-next-line
  }, [load, payload]);

  // Handle the response
  useEffect(() => {
    if (response) {
      /*
      let receivedPayload;
      try {
        receivedPayload = JSON.parse(content); // Parse the JSON string back to an object
      } catch (parseError) {
        console.error('Failed to parse file content as JSON:', parseError);
        alert('Failed to parse file content as JSON. The file may be corrupted or in an unsupported format.');
        setReset(true);
        return;
      }
        */
      const receivedPayload = JSON.parse(response);

      // DEBUG: extra delay to inspect the loading popup — remove before shipping
      setTimeout(() => {
        dispatch(ReducersIndex.uiReducers.closeModal('loading'));
        dispatch(ReducersIndex.uiReducers.clearModals());
        setLoad(false);

        if (receivedPayload.success) {
          dispatch(ReducersIndex.uiReducers.setParams(JSON.parse(response)));
          setIsLoadingDesign(true);
          dispatch(ReducersIndex.container.setProjectData(receivedPayload));

          // Override the default landing menu and set view to Microphones
          // Use setTimeout to ensure this runs after handleContainerSetProjectData completes
          setTimeout(() => {
            dispatch(ReducersIndex.uiReducers.setCurrentView('microphones'));
          }, 0);

          // Reset after all load-triggered RAF calculations settle (~200ms max).
          setTimeout(() => setIsLoadingDesign(false), 600);

          setFirstTimeUser(false)
          if (!receivedPayload.latestrevision) {
            dispatch(ReducersIndex.uiReducers.openModal('oldRevision'));
          } else {
            setProjectLoaded(true);
          };
        } else {
          if (receivedPayload.errornumber !== 7) {
            showError(`An error occurred while sending data to the server. Error code: ${receivedPayload.errornumber} ${receivedPayload.errortext}.`);
            setReset(true);
          } else {
            dispatch(ReducersIndex.uiReducers.openModal('unknowRevision'));
          };
        };
      }, 300);
    };
  // eslint-disable-next-line
  }, [response]);


  // Handle reload 
  useEffect(() => {
    if (isBoolean(reload)) {
      if (reload) {
        const {revision, ...rest} = payload;
        setPayload(rest);
        setLoad(true);
        dispatch(ReducersIndex.uiReducers.setReload(false));
      } else {
        if (isLatest) {
          setReset(true);
          setProjectLoaded(true);
        } else {
          setProjectLoaded(true);
        };
      };
    };
  // eslint-disable-next-line
  }, [reload]);

  // handle UI reset 
  // note: this is temporary to handle UI reset when somethinggoes wrong. Might change 
  useEffect(() => {
    if (reset ) {
      dispatch(ReducersIndex.container.setRecalculate({flag: false}));
      dispatch(ReducersIndex.uiReducers.setExitToRoom(false));
      // Only open target modal for non-Conference Room Designer tools (CRD uses walkthrough guide)
      if (currentTool !== 'conferenceroomDesigner' && target) {
        dispatch(ReducersIndex.uiReducers.openModal(target));
      }
      setProjectLoaded(true);
    }
  // eslint-disable-next-line
  }, [reset, errorState]);

  // Handle network errors 
  useEffect(() => {
    if (error) {
      console.error(`Error loading design ${payload.savekey}:`, error);
      console.log(`Current payload:`, payload);
      console.log(`Attempted URL: ${url}`);

      // Perform health check and show appropriate error message
      const handleFinalError = async () => {
        dispatch(ReducersIndex.uiReducers.closeModal('loading'));
        dispatch(ReducersIndex.uiReducers.clearModals());
        
        try {
          const isServiceHealthy = await checkStorageServiceHealth();
          const errorMessage = getDetailedErrorMessage(error, isServiceHealthy);
          console.error(`Final error message: ${errorMessage}`);
          showError(errorMessage);
        } catch (healthCheckError) {
          console.error('Health check failed:', healthCheckError);
          // Fallback to basic error message if health check fails
          const basicErrorMessage = `An error occurred while loading the design: ${error}\n\nPlease check that the storage service is running and accessible.`;
          console.error(`Fallback error message: ${basicErrorMessage}`);
          showError(basicErrorMessage);
        }
        
        setReset(true);
      };
      
      handleFinalError();
    };
  // eslint-disable-next-line
  }, [error, payload.revision]);
}

export default Load;
